import React, { useState, useEffect, useCallback } from "react";
import { useSelector, useDispatch } from "react-redux";
import Moment from "moment";
import axiosInstance from "../../config/axiosApi/axiosApi";
import APIs from "../../Constants/apis";
import showErrorToast, { showSuccessToast } from "../../components/CustomComponents/toast/toast";
import GridSectionLoader from "../../components/CustomComponents/preLoad/gridSectionLoader";
import ServeIQLoader from "../../components/CustomComponents/preLoad/serveiqLoader";
import { useProfileAndSettings } from "../../hooks/useProfileAndSettings";
import CustomModal from "../../components/CustomComponents/modal/customModal";
import ModernGridToolbar from "../../components/CustomComponents/ModernGridToolbar/ModernGridToolbar";
import ModernGridPagerFooter from "../../components/CustomComponents/ModernGridToolbar/ModernGridPagerFooter";
import SortableColumnHeader from "../../components/CustomComponents/ModernGridToolbar/SortableColumnHeader";
import useResetPageOnFilterChange from "../../hooks/useResetPageOnFilterChange";
import useDebouncedEffect from "../../hooks/useDebouncedEffect";
import useThemeColor from "../../hooks/useThemeColor";
import useDarkMode from "../../hooks/useDarkMode";
import {
  setSortOrder,
  setSortBy,
  setFilterField,
} from "../../app/itemGridPreferencesSlice";
import "./zReport.css";

const Z_REPORT_SORT_FIELDS = {
  toDateTime: "ToDateTime",
  grossSales: "GrossSales",
  totalOrders: "TotalOrders",
  cashPosition: "CashPosition",
};

const Z_REPORT_SORT_LABELS = {
  [Z_REPORT_SORT_FIELDS.toDateTime]: "Date",
  [Z_REPORT_SORT_FIELDS.grossSales]: "Gross Sales",
  [Z_REPORT_SORT_FIELDS.totalOrders]: "Total Orders",
  [Z_REPORT_SORT_FIELDS.cashPosition]: "Cash Position",
};

const ZReport = () => {
  const CompID = useSelector((state) => state.auth.user)?.userData?.compID;
  const { getCompCurrency } = useProfileAndSettings();
  const compCurrency = getCompCurrency();
  const themeColor = useThemeColor();
  const isDarkMode = useDarkMode();
  const dispatch = useDispatch();

  const pageKey = "zReport";
  const defaultSortOrder = { value: "DESC", label: "DESC" };
  const defaultSortBy = { value: "ToDateTime", label: "Date" };
  const defaultPageSize = { value: "50", label: "50" };
  const validPageSizes = ["20", "50", "100"];

  const preferences = useSelector((state) => state.itemGridPreferences[pageKey] || {});
  const sortOrder = preferences.sortOrder || defaultSortOrder;
  const sortBy = preferences.sortBy || defaultSortBy;
  const pageSize =
    preferences.pageSize &&
    validPageSizes.includes(String(preferences.pageSize.value))
      ? preferences.pageSize
      : defaultPageSize;

  const [loading, setLoading] = useState(false);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [actionLoading, setActionLoading] = useState(false);
  const [zReportData, setZReportData] = useState(null);
  const [zReportHistory, setZReportHistory] = useState([]);
  const [showPreviewModal, setShowPreviewModal] = useState(false);
  const [showDetailsModal, setShowDetailsModal] = useState(false);
  const [selectedZReport, setSelectedZReport] = useState(null);

  const [pageNumber, setPageNumber] = useState(1);
  const [totalPages, setTotalPages] = useState(0);
  const [totalRecords, setTotalRecords] = useState(0);
  const [search, setSearch] = useState("");

  const handlePageSizeChange = useCallback(
    (value) => {
      dispatch(setFilterField({ pageKey, field: "pageSize", value }));
    },
    [dispatch, pageKey]
  );

  const handleColumnSort = useCallback(
    (sortField) => {
      const nextOrder =
        sortBy?.value === sortField && sortOrder?.value === "DESC"
          ? { value: "ASC", label: "ASC" }
          : { value: "DESC", label: "DESC" };

      dispatch(
        setSortBy({
          pageKey,
          sortBy: {
            value: sortField,
            label: Z_REPORT_SORT_LABELS[sortField] ?? sortField,
          },
        })
      );
      dispatch(setSortOrder({ pageKey, sortOrder: nextOrder }));
    },
    [dispatch, pageKey, sortBy?.value, sortOrder?.value]
  );

  const currencySymbols = {
    GBP: "£",
    USD: "$",
    EUR: "€",
  };

  const getCurrencySymbol = (currencyCode) => {
    return currencySymbols[currencyCode] || currencyCode;
  };

  const formatCurrency = (value) => {
    if (value === null || value === undefined || isNaN(value)) return `${getCurrencySymbol(compCurrency)}0.00`;
    return `${getCurrencySymbol(compCurrency)}${parseFloat(value).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  };

  const fetchZReportPreview = useCallback(async () => {
    if (!CompID) return;
    try {
      setPreviewLoading(true);
      const response = await axiosInstance.get(APIs.GET_Z_REPORT_PREVIEW, {
        params: { CompID }
      });
      if (response.data.status) {
        setZReportData(response.data.data);
      } else {
        showErrorToast(response.data.message || "Failed to fetch Z-Report preview");
      }
    } catch (err) {
      showErrorToast("Failed to load Z-Report preview");
    } finally {
      setPreviewLoading(false);
    }
  }, [CompID]);

  const runZReport = useCallback(async () => {
    if (!CompID) return;
    if (!window.confirm("Are you sure you want to run the Z-Report? This will save the current period's data.")) {
      return;
    }
    try {
      setActionLoading(true);
      const response = await axiosInstance.post(APIs.RUN_Z_REPORT, null, {
        params: { CompID }
      });
      if (response.data.status) {
        showSuccessToast("Z-Report generated successfully!");
        await fetchZReportPreview();
        const historyResponse = await axiosInstance.get(APIs.GET_Z_REPORT_HISTORY_PAGINATED, {
          params: {
            CompID,
            PageNumber: pageNumber,
            PageSize: pageSize.value,
            Search: search?.trim() || "",
            SortBy: sortBy.value || sortBy,
            SortOrder: sortOrder.value || sortOrder
          }
        });
        if (historyResponse.data.status) {
          setZReportHistory(historyResponse.data.data || []);
          setTotalPages(historyResponse.data.totalPages || 0);
          setTotalRecords(historyResponse.data.totalRecords || 0);
        }
        setShowPreviewModal(false);
      } else {
        showErrorToast(response.data.message || "Failed to generate Z-Report");
      }
    } catch (err) {
      showErrorToast("Failed to generate Z-Report");
    } finally {
      setActionLoading(false);
    }
  }, [CompID, fetchZReportPreview, pageNumber, pageSize, search, sortBy, sortOrder]);

  const fetchZReportHistory = useCallback(async () => {
    if (!CompID) return;
    try {
      setLoading(true);
      const response = await axiosInstance.get(APIs.GET_Z_REPORT_HISTORY_PAGINATED, {
        params: {
          CompID,
          PageNumber: pageNumber,
          PageSize: pageSize.value,
          Search: search?.trim() || "",
          SortBy: sortBy.value || sortBy,
          SortOrder: sortOrder.value || sortOrder
        }
      });
      if (response.data.status) {
        setZReportHistory(response.data.data || []);
        setTotalPages(response.data.totalPages || 0);
        setTotalRecords(response.data.totalRecords || 0);
      } else {
        showErrorToast(response.data.message || "Failed to fetch Z-Report history");
      }
    } catch (err) {
      showErrorToast("Failed to load Z-Report history");
    } finally {
      setLoading(false);
    }
  }, [CompID, pageNumber, pageSize, search, sortBy, sortOrder]);

  useEffect(() => {
    if (!preferences.sortOrder) {
      dispatch(setSortOrder({ pageKey, sortOrder: defaultSortOrder }));
    }
    if (!preferences.sortBy) {
      dispatch(setSortBy({ pageKey, sortBy: defaultSortBy }));
    }
    if (
      !preferences.pageSize ||
      !validPageSizes.includes(String(preferences.pageSize?.value))
    ) {
      dispatch(setFilterField({ pageKey, field: "pageSize", value: defaultPageSize }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dispatch, pageKey]);

  useResetPageOnFilterChange(setPageNumber, [
    search,
    sortBy?.value,
    sortOrder?.value,
    pageSize?.value,
  ]);

  useDebouncedEffect(() => fetchZReportHistory(), [fetchZReportHistory]);

  const handleViewDetails = (report) => {
    setSelectedZReport(report);
    setShowDetailsModal(true);
  };

  const handleOpenPreview = () => {
    fetchZReportPreview();
    setShowPreviewModal(true);
  };

  const handlePrint = async (zReportDataToPrint = null) => {
    if (!CompID) return;
    try {
      setActionLoading(true);
      const dataToPrint = zReportDataToPrint || zReportData || selectedZReport;
      if (!dataToPrint) {
        showErrorToast("No Z-Report data available to print");
        return;
      }
      const zReportId = dataToPrint.id && dataToPrint.id > 0 ? dataToPrint.id : null;
      const response = await axiosInstance.get(APIs.GENERATE_Z_REPORT_PDF, {
        params: { CompID, ZReportID: zReportId },
        responseType: 'blob'
      });
      if (response.data instanceof Blob && response.data.type === "application/pdf") {
        const url = window.URL.createObjectURL(response.data);
        const link = document.createElement('a');
        link.href = url;
        link.download = zReportId
          ? `Z-Report-${zReportId}-${Moment(dataToPrint.toDateTime).format('YYYYMMDD-HHmmss')}.pdf`
          : `Z-Report-Preview-${Moment().format('YYYYMMDD-HHmmss')}.pdf`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);
        showSuccessToast("PDF downloaded successfully");
      } else {
        showErrorToast("Failed to generate PDF");
      }
    } catch (err) {
      showErrorToast("Failed to generate PDF");
    } finally {
      setActionLoading(false);
    }
  };

  const renderMetricRows = (rows, { emphasizeLast = false } = {}) => (
    <div className="zr-metric-list">
      {rows.map((row, index) => {
        const isLast = emphasizeLast && index === rows.length - 1;
        return (
          <div
            key={row.label}
            className={`zr-metric-row${isLast ? " zr-metric-row--emphasis" : ""}`}
          >
            <span className="zr-metric-label">{row.label}</span>
            <span
              className="zr-metric-value"
              style={row.accent ? { color: themeColor } : undefined}
            >
              {row.value}
            </span>
          </div>
        );
      })}
    </div>
  );

  const renderZReportContent = (data) => {
    if (!data) return null;

    const netVat = Number(data.netVat ?? data.NetVat ?? data.vat ?? 0);
    const closingBalance = Number(data.floatClosingBalance ?? data.floatAmount ?? 0);

    const tenderRows = [
      { label: "Card", value: formatCurrency(data.card_NetSales ?? data.card_GrossSales) },
      { label: "Cash", value: formatCurrency(data.cash_NetSales ?? data.cash_GrossSales) },
      { label: "Online", value: formatCurrency(data.online_NetSales ?? data.online_GrossSales) },
    ];
    if (Number(data.thirdParty_NetSales ?? data.thirdParty_GrossSales ?? 0) !== 0) {
      tenderRows.push({
        label: "Third-Party",
        value: formatCurrency(data.thirdParty_NetSales ?? data.thirdParty_GrossSales),
      });
    }

    const advanceDepositCash = Number(data.advanceDeposit_Cash ?? data.AdvanceDeposit_Cash ?? 0);
    const advanceDepositCard = Number(data.advanceDeposit_Card ?? data.AdvanceDeposit_Card ?? 0);
    const advanceDepositOnline = Number(data.advanceDeposit_Online ?? data.AdvanceDeposit_Online ?? 0);
    const advanceDepositTotal = Number(
      data.advanceDeposit_Total ?? data.AdvanceDeposit_Total ?? data.deposit_GrossSales ?? 0
    );
    const depositApplied = Number(data.depositApplied ?? data.DepositApplied ?? 0);

    const orderRows = [
      { label: "Completed", value: data.completedOrders || 0, accent: true },
      { label: "Cancelled", value: data.cancelledOrders || 0, accent: true },
      { label: "Refunds", value: data.refundedOrders || 0, accent: true },
      { label: "Running", value: data.runningOrders || 0, accent: true },
    ];
    if (Number(data.draftOrders || 0) > 0) {
      orderRows.push({ label: "Draft", value: data.draftOrders || 0, accent: true });
    }

    return (
      <div className={`z-report-content zr-layout ${isDarkMode ? "z-report-dark" : ""}`}>
        <div className="zr-grid">
          <div className="zr-card zr-card--hero">
            <div className="zr-hero-block">
              <span className="zr-hero-label">Total Sales</span>
              <span className="zr-hero-value" style={{ color: themeColor }}>
                {formatCurrency(data.grossSales)}
              </span>
            </div>
            <div className="zr-hero-divider" />
            <div className="zr-hero-block">
              <span className="zr-hero-label">VAT</span>
              <span className="zr-hero-value" style={{ color: themeColor }}>
                {formatCurrency(netVat)}
              </span>
            </div>
          </div>

          <div className="zr-card">
            <div className="zr-card-header">
              <h4 style={{ color: themeColor }}>Tender Summary</h4>
            </div>
            {renderMetricRows(tenderRows)}
          </div>

          <div className="zr-card">
            <div className="zr-card-header">
              <h4 style={{ color: themeColor }}>Till Summary</h4>
            </div>
            {renderMetricRows(
              [
                { label: "Opening Balance", value: formatCurrency(data.floatOpeningBalance || 0) },
                { label: "Deposits", value: formatCurrency(data.floatDeposited || 0) },
                { label: "Withdrawn", value: formatCurrency(data.floatWithdraw || 0) },
                { label: "Petty Cash", value: formatCurrency(data.pettyCash || 0) },
                {
                  label: "Closing Balance",
                  value: formatCurrency(closingBalance),
                  accent: true,
                },
              ],
              { emphasizeLast: true }
            )}
          </div>

          <div className="zr-card">
            <div className="zr-card-header">
              <h4 style={{ color: themeColor }}>Total Order Count</h4>
              <span className="zr-card-badge" style={{ borderColor: `${themeColor}55`, color: themeColor }}>
                {data.totalOrders || 0}
              </span>
            </div>
            {renderMetricRows(orderRows)}
          </div>

          <div className="zr-card">
            <div className="zr-card-header">
              <h4 style={{ color: themeColor }}>Deduction &amp; Add-ons</h4>
            </div>
            {renderMetricRows([
              { label: "Tips", value: formatCurrency(data.tipAmount || 0) },
              { label: "Discounts", value: formatCurrency(data.discountAmount || 0) },
              { label: "Vouchers", value: formatCurrency(data.voucherDiscountAmount || 0) },
              { label: "Refunds", value: formatCurrency(data.refundAmount || 0) },
            ])}
          </div>

          <div className="zr-card">
            <div className="zr-card-header">
              <h4 style={{ color: themeColor }}>Fee &amp; Surcharge</h4>
            </div>
            {renderMetricRows([
              { label: "Service Charges", value: formatCurrency(data.serviceCharges || 0) },
              { label: "Delivery Charges", value: formatCurrency(data.deliveryCharges || 0) },
              { label: "Extra Charges", value: formatCurrency(data.extraCharges || 0) },
            ])}
          </div>

          <div className="zr-card">
            <div className="zr-card-header">
              <h4 style={{ color: themeColor }}>Deposit</h4>
              <span className="zr-card-badge" style={{ borderColor: `${themeColor}55`, color: themeColor }}>
                {formatCurrency(advanceDepositTotal)}
              </span>
            </div>
            {renderMetricRows(
              [
                { label: "Deposit Received", value: formatCurrency(advanceDepositTotal), accent: true },
                { label: "  Cash", value: formatCurrency(advanceDepositCash) },
                { label: "  Card", value: formatCurrency(advanceDepositCard) },
                { label: "  Online", value: formatCurrency(advanceDepositOnline) },
                { label: "Deposit Applied", value: formatCurrency(depositApplied), accent: true },
              ],
              { emphasizeLast: true }
            )}
          </div>
        </div>

        <div className="zr-footer-stats">
          <div className="zr-footer-stat">
            <span className="zr-footer-label">Net Sales</span>
            <span className="zr-footer-value">{formatCurrency(data.netSales)}</span>
          </div>
          <div className="zr-footer-stat">
            <span className="zr-footer-label">Cash Position</span>
            <span className="zr-footer-value" style={{ color: themeColor }}>
              {formatCurrency(data.cashPosition || 0)}
            </span>
          </div>
        </div>
      </div>
    );
  };

  const renderPreviewHeader = (data) => (
    <div
      style={{
        borderRadius: "14px",
        padding: "16px 18px",
        marginBottom: "16px",
        background: isDarkMode
          ? "linear-gradient(135deg, #1f2a33 0%, #25333e 100%)"
          : "linear-gradient(135deg, #f8fbff 0%, #eef5ff 100%)",
        border: `1px solid ${themeColor}33`,
        boxShadow: `0 6px 18px ${themeColor}22`,
      }}
    >
      <div className="d-flex justify-content-between align-items-start flex-wrap gap-3">
        <div>
          <h5
            className="mb-1"
            style={{
              color: isDarkMode ? "#e9ecef" : "#1f2937",
              fontWeight: 700,
            }}
          >
            Z Report
          </h5>
          <div
            style={{
              color: isDarkMode ? "#adb5bd" : "#64748b",
              fontSize: "13px",
              fontWeight: 600,
            }}
          >
            {Moment(data.fromDateTime).format("MMM D, YYYY HH:mm")}
            {" – "}
            {Moment(data.toDateTime).format("MMM D, YYYY HH:mm")}
          </div>
        </div>
        <div className="d-flex gap-2 flex-wrap">
          <button
            className="btn"
            onClick={() => handlePrint(data)}
            disabled={actionLoading}
            style={{
              backgroundColor: isDarkMode ? "#334155" : "#ffffff",
              border: `1px solid ${isDarkMode ? "#475569" : "#cbd5e1"}`,
              color: isDarkMode ? "#e2e8f0" : "#334155",
              borderRadius: "10px",
              padding: "10px 18px",
              fontWeight: 600,
            }}
          >
            <i className="fa fa-print me-2"></i>
            Print
          </button>
          <button
            className="btn"
            onClick={runZReport}
            disabled={actionLoading}
            style={{
              backgroundColor: isDarkMode ? "#198754" : "#157347",
              border: "none",
              color: "white",
              borderRadius: "10px",
              padding: "10px 18px",
              fontWeight: 600,
            }}
          >
            <i className="fa fa-play me-2"></i>
            Run Z Report
          </button>
        </div>
      </div>
    </div>
  );

  return (
    <div>
      <div className="d-flex align-items-center mb-3">
        <div>
          <ol className="breadcrumb">
            <li className="breadcrumb-item">
              <a href="#/">Home</a>
            </li>
            <li className="breadcrumb-item active">Z-Report</li>
          </ol>
          <h1 className="page-header mb-0">Z-Report</h1>
        </div>
        <div className="ms-auto">
          <div className="d-flex justify-content-end align-items-center mb-3 gap-2">
            <button
              className="btn px-4"
              onClick={handleOpenPreview}
              style={{
                backgroundColor: themeColor,
                background: `linear-gradient(135deg, ${themeColor} 0%, ${themeColor}dd 100%)`,
                border: 'none',
                color: 'white',
                borderRadius: '8px',
                padding: '8px 16px',
                fontSize: '14px',
                fontWeight: '600',
                boxShadow: `0 2px 8px ${themeColor}40`
              }}
            >
              <i className="fas fa-eye fa-lg me-2 ms-n2 text-white"></i>
              Preview Z-Report
            </button>
          </div>
        </div>
      </div>

      <div className="mb-3 mt-2">
        <ModernGridToolbar
          isDarkMode={isDarkMode}
          title="Z-Report History"
          totalCount={totalRecords}
          searchValue={search}
          onSearchChange={setSearch}
          searchPlaceholder="Search by ID, Gross Sales, Net Sales, Total Orders..."
          bodyFlush
        >
          <div className="px-3 pb-3">
            <GridSectionLoader loading={loading} minHeight="280px">
              {zReportHistory.length === 0 && !loading ? (
                <div className="alert alert-info text-center mt-3">
                  <i className="fa fa-info-circle me-2"></i>
                  No Z-Reports found
                </div>
              ) : (
                <>
                  <div
                    className="table-responsive table-container"
                    style={{
                      minHeight: "200px",
                      maxHeight: "450px",
                      overflowY: "auto",
                      overflowX: "auto",
                    }}
                  >
                    <table
                      className="table table-hover"
                      style={{ "--modern-grid-sort-active": themeColor }}
                    >
                      <thead
                        className="bg-light sticky-header"
                        style={{
                          position: "sticky",
                          top: 0,
                          zIndex: 10000,
                          backgroundColor: isDarkMode ? "#2d3436" : "#f8f9fa",
                          color: isDarkMode ? "#ffffff" : "#000000",
                        }}
                      >
                        <tr>
                          <th className="pt-2 pb-2">From Date</th>
                          <SortableColumnHeader
                            sortField={Z_REPORT_SORT_FIELDS.toDateTime}
                            sortBy={sortBy}
                            sortOrder={sortOrder}
                            onSort={handleColumnSort}
                          >
                            To Date
                          </SortableColumnHeader>
                          <SortableColumnHeader
                            sortField={Z_REPORT_SORT_FIELDS.grossSales}
                            sortBy={sortBy}
                            sortOrder={sortOrder}
                            onSort={handleColumnSort}
                            className="text-center"
                          >
                            Gross Sales
                          </SortableColumnHeader>
                          <th className="pt-2 pb-2 text-center">Net Sales</th>
                          <SortableColumnHeader
                            sortField={Z_REPORT_SORT_FIELDS.totalOrders}
                            sortBy={sortBy}
                            sortOrder={sortOrder}
                            onSort={handleColumnSort}
                            className="text-center"
                          >
                            Total Orders
                          </SortableColumnHeader>
                          <SortableColumnHeader
                            sortField={Z_REPORT_SORT_FIELDS.cashPosition}
                            sortBy={sortBy}
                            sortOrder={sortOrder}
                            onSort={handleColumnSort}
                            className="text-center"
                          >
                            Cash Position
                          </SortableColumnHeader>
                          <th className="pt-2 pb-2 text-center">Actions</th>
                        </tr>
                      </thead>
                      <tbody>
                        {zReportHistory.length === 0 ? (
                          <tr>
                            <td colSpan="7" className="text-center">No Z-Reports found</td>
                          </tr>
                        ) : (
                          zReportHistory.map((report) => (
                            <tr key={report.id}>
                              <td className="align-middle">
                                <div style={{ fontSize: '13px', color: '#495057' }}>{Moment(report.fromDateTime).format('DD MMM YYYY')}</div>
                                <div style={{ fontSize: '12px', color: '#6c757d' }}>{Moment(report.fromDateTime).format('HH:mm')}</div>
                              </td>
                              <td className="align-middle">
                                <div style={{ fontSize: '13px', color: '#495057' }}>{Moment(report.toDateTime).format('DD MMM YYYY')}</div>
                                <div style={{ fontSize: '12px', color: '#6c757d' }}>{Moment(report.toDateTime).format('HH:mm')}</div>
                              </td>
                              <td className="align-middle text-center">{formatCurrency(report.grossSales)}</td>
                              <td className="align-middle text-center">{formatCurrency(report.netSales)}</td>
                              <td className="align-middle text-center">{report.totalOrders}</td>
                              <td className="align-middle text-center">{formatCurrency(report.cashPosition)}</td>
                              <td className="align-middle text-center">
                                <button
                                  className="btn btn-sm"
                                  onClick={() => handleViewDetails(report)}
                                  title="View Details"
                                  style={{
                                    width: '32px', height: '32px', display: 'flex', alignItems: 'center', justifyContent: 'center',
                                    borderRadius: '6px', border: `1px solid ${themeColor}`, backgroundColor: 'transparent', color: themeColor
                                  }}
                                >
                                  <i className="fa fa-eye" style={{ fontSize: '12px' }}></i>
                                </button>
                              </td>
                            </tr>
                          ))
                        )}
                      </tbody>
                    </table>
                  </div>
                  <ModernGridPagerFooter
                    pageSize={pageSize}
                    onPageSizeChange={handlePageSizeChange}
                    totalRecords={totalRecords}
                    pageNumber={pageNumber}
                    totalPages={totalPages}
                    setPageNumber={setPageNumber}
                  />
                </>
              )}
            </GridSectionLoader>
          </div>
        </ModernGridToolbar>
      </div>

      <CustomModal isOpen={showPreviewModal} onClose={() => setShowPreviewModal(false)} title="Z-Report Preview" modalSize="modal-xl" maxHeight="90vh" showButton={false}>
        <div
          className={`z-report-modal-content ${isDarkMode ? 'z-report-modal-dark' : ''}`}
          style={{
            background: isDarkMode ? "#161b22" : "#f6f8fb",
            borderRadius: "12px",
            padding: "14px",
          }}
        >
          {previewLoading && !zReportData ? (
            <div
              className="d-flex flex-column align-items-center justify-content-center gap-2"
              style={{ minHeight: "260px" }}
              role="status"
              aria-live="polite"
            >
              <ServeIQLoader size={56} />
              <div
                style={{
                  fontSize: "13px",
                  fontWeight: 600,
                  color: isDarkMode ? "#adb5bd" : "#64748b",
                }}
              >
                Loading Z-Report preview…
              </div>
            </div>
          ) : zReportData ? (
            <>
              {renderPreviewHeader(zReportData)}
              <div
                style={{
                  background: isDarkMode ? "#10161d" : "#ffffff",
                  borderRadius: "12px",
                  padding: "10px",
                  border: isDarkMode ? "1px solid #2b3440" : "1px solid #e9ecef",
                }}
              >
                {renderZReportContent(zReportData)}
              </div>
            </>
          ) : (
            <div className="alert alert-info text-center mb-0">
              <i className="fa fa-info-circle me-2"></i>
              No Z-Report data is available for preview at the moment.
            </div>
          )}
        </div>
      </CustomModal>

      <CustomModal isOpen={showDetailsModal} onClose={() => setShowDetailsModal(false)} title="Z-Report Summary" modalSize="modal-xl" maxHeight="90vh" showButton={false}>
        <div
          className={`z-report-modal-content ${isDarkMode ? "z-report-modal-dark" : ""}`}
          style={{
            background: isDarkMode ? "#161b22" : "#f6f8fb",
            borderRadius: "12px",
            padding: "14px",
          }}
        >
          {selectedZReport && (
            <>
              <div className="d-flex justify-content-between align-items-center flex-wrap gap-2 mb-3">
                <div className="z-report-timestamp mb-0">
                  <span style={{ color: isDarkMode ? "#adb5bd" : "#64748b", fontWeight: 600 }}>
                    {Moment(selectedZReport.fromDateTime).format("MMM D, YYYY HH:mm")}
                    {" – "}
                    {Moment(selectedZReport.toDateTime).format("MMM D, YYYY HH:mm")}
                  </span>
                </div>
                <button
                  className="btn"
                  onClick={() => handlePrint(selectedZReport)}
                  style={{
                    backgroundColor: isDarkMode ? "#334155" : "#ffffff",
                    border: `1px solid ${isDarkMode ? "#475569" : "#cbd5e1"}`,
                    color: isDarkMode ? "#e2e8f0" : "#334155",
                    borderRadius: "10px",
                    padding: "10px 18px",
                    fontWeight: 600,
                  }}
                >
                  <i className="fa fa-print me-2"></i>
                  Print
                </button>
              </div>
              {renderZReportContent(selectedZReport)}
            </>
          )}
        </div>
      </CustomModal>
    </div>
  );
};

export default ZReport;
